// src/encounter/structured-note/occurrence-layout.ts
//
// PROD-2824 · TS-5 · design desk D-65 (5 Oct). How a phrase OCCURRENCE lays out its words and parts in
// the draft card. LAYOUT ONLY: the text Complete writes comes from the ops (`draftText`) and never from
// this — the library's sentence is clinical content and the signed note keeps it as written.
//
//   1. FINDING ROWS. A pick whose options are all system "cps-finding-state" (absent / present / not
//      asked) draws as one row — its label, then its options — one finding per row, options aligned in a
//      column. The text before it loses its trailing "<label>:" and any leftover that is only
//      punctuation; what remains becomes the line above the rows ("Cardiovascular —" → "Cardiovascular").
//      If the text before does not end with the pick's label, the pick stays inline. No guessing.
//   2. PUNCTUATION NEVER STARTS A LINE (every pick, every part): a text run's leading separator stays on
//      the line of the part before it — glued to that part — so no row starts with "; " or ".". After a
//      finding row (a line of its own) a punctuation-only leftover is dropped, as is a line break.
import { embeddedPiece, type BlankPiece, type Piece, type PickPiece } from '../lib/note-pieces';
import type { DeltaOp } from '../lib/note-delta';

/** The code system of a finding state (absent / present / not asked). */
export const FINDING_STATE_SYSTEM = 'cps-finding-state';

export type LayoutItem =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'piece'; key: string; index: number; piece: Piece; suffix: string }
  | { kind: 'findings'; key: string; rows: Array<{ index: number; piece: PickPiece }> };

/** A pick that offers only finding states (each option of system "cps-finding-state"). */
export function isFindingPick(piece: Piece): piece is PickPiece {
  return piece.t === 'pick' && piece.options.length > 0 && piece.options.every((o) => (o as { system?: unknown }).system === FINDING_STATE_SYSTEM);
}

const LEADING_PUNCT = /^[.,;:!?]+/;
const ONLY_PUNCT_SPACE = /^[\s.,;:!?–—-]*$/;
const TRAILING_PUNCT_SPACE = /[\s.,;:!?–—-]+$/u;

/** If `text` ends with "<label>:" (spaces after allowed), the text before it; else undefined. */
export function cutTrailingLabel(text: string, label: string): string | undefined {
  const trimmed = text.replace(/\s+$/, '');
  const tail = `${label}:`;
  if (!label || !trimmed.endsWith(tail)) return undefined;
  const before = trimmed.slice(0, -tail.length);
  // The label must start a word (not the end of a longer one: "pain:" in "chest pain:" is fine only as
  // the whole label).
  if (before && /[\p{L}\p{N}]$/u.test(before)) return undefined;
  return before;
}

type Token = { t: 'text'; text: string; index: number } | { t: 'piece'; index: number; piece: Piece };

/** The occurrence's layout items, from its ops. */
export function occurrenceLayout(ops: readonly DeltaOp[]): LayoutItem[] {
  const tokens: Token[] = [];
  ops.forEach((op, index) => {
    const piece = embeddedPiece(op);
    if (piece) tokens.push({ t: 'piece', index, piece });
    else if (typeof op.insert === 'string') {
      // Text runs back to back are one run of words.
      const prev = tokens[tokens.length - 1];
      if (prev?.t === 'text') prev.text += op.insert;
      else tokens.push({ t: 'text', text: op.insert, index });
    }
  });
  // The phrase's terminal newline is not part of the card.
  const last = tokens[tokens.length - 1];
  if (last?.t === 'text') last.text = last.text.replace(/\n+$/, '');

  // 1 · which picks become finding rows (their label ends the text before them).
  const isRow = new Set<number>();
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (tok.t !== 'piece' || !isFindingPick(tok.piece)) continue;
    const prev = tokens[i - 1];
    if (prev?.t !== 'text') continue;
    const before = cutTrailingLabel(prev.text, tok.piece.label);
    if (before === undefined) continue;
    isRow.add(i);
    prev.text = ONLY_PUNCT_SPACE.test(before) ? '' : before.replace(TRAILING_PUNCT_SPACE, '');
  }

  const items: LayoutItem[] = [];
  let afterRow = false;
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (tok.t === 'text') {
      let text = tok.text;
      if (afterRow) {
        // After a finding row (its own line): drop a punctuation-only lead and the line break after it.
        text = text.replace(LEADING_PUNCT, '').replace(/^[ \t]*\n/, '');
      } else {
        // 2 · glue a leading separator to the part before it.
        const lead = LEADING_PUNCT.exec(text)?.[0];
        const before = items[items.length - 1];
        if (lead && before?.kind === 'piece') {
          before.suffix += lead;
          text = text.slice(lead.length);
        }
      }
      afterRow = false;
      if (text) items.push({ kind: 'text', key: `t${tok.index}`, text });
      continue;
    }
    if (isRow.has(i)) {
      const block = items[items.length - 1];
      const row = { index: tok.index, piece: tok.piece as PickPiece };
      if (block?.kind === 'findings') block.rows.push(row);
      else items.push({ kind: 'findings', key: `f${tok.index}`, rows: [row] });
      afterRow = true;
      continue;
    }
    afterRow = false;
    items.push({ kind: 'piece', key: `p${tok.index}`, index: tok.index, piece: tok.piece, suffix: '' });
  }
  // A finding block absorbs nothing but empty text between its rows (dropped above), so rows that were
  // written back to back stay one aligned block.
  return items;
}

/**
 * The chosen chip's DS variant. A FINDING pick (D-65 amendment 0, interim — swaps to a signal-ds
 * FindingStateChip under PROD-2987): a chosen "present" is error SOLID, a chosen "absent" or "not asked"
 * neutral SOLID — solid grey reads as "answered", red marks only the positive findings, and "✓" means
 * chosen on all three, so the state is never colour alone (D-38). Any other pick keeps the primary
 * chosen chip. Unchosen: neutral soft, the word only.
 */
export function chosenChip(piece: PickPiece, code: string): { semantic: 'error' | 'neutral' | 'primary'; tone: 'solid' } {
  if (!isFindingPick(piece)) return { semantic: 'primary', tone: 'solid' };
  return { semantic: code === 'present' ? 'error' : 'neutral', tone: 'solid' };
}

/**
 * D-65 (b): a blank is as wide as its placeholder or label, at least 12rem; wider than 60% of the line,
 * it takes its own full line. It never clips its placeholder. (1ch ≈ the width of a digit; the label is
 * measured in characters, with room for the field's padding.)
 */
export function blankFit(piece: BlankPiece, lineWidth: number | undefined): { full: boolean; width: string } {
  const chars = Math.max(piece.label.length, (piece.unit ? '__' : piece.label).length);
  const widthPx = Math.max(192, (chars + 3) * 7.8);
  if (lineWidth && widthPx > 0.6 * lineWidth) return { full: true, width: '100%' };
  return { full: false, width: `max(12rem, ${chars + 3}ch)` };
}
